// /opt/isp_erp/frontend/src/components/tickets/CreateTicketDialog.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Autocomplete,      // ✅ NUEVO
  Paper,
  Typography
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { ticketsAPI } from '../../api/tickets.api';
import ClienteBuscador from '../ClienteBuscador';

export default function CreateTicketDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    description: '',
    ticket_type: 'incidente',
    problem_origin: 'corte_total',
    priority: 'media',
    contact_phone: '',
    contact_phone_alt: ''
  });

  useEffect(() => {
    if (open) {
      loadCustomers();
      setSelectedCustomer(null);
      setFormData({
        customer_id: '',
        title: '',
        description: '',
        ticket_type: 'incidente',
        problem_origin: 'corte_total',
        priority: 'media',
        contact_phone: '',
        contact_phone_alt: ''
      });
    }
  }, [open]);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const data = [];
      console.log('✅ Clientes cargados:', data?.length || 0);
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError('Error al cargar clientes');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customer?.id || '',
      contact_phone: customer?.telefono || customer?.phone_mobile || '',
      contact_phone_alt: customer?.phone_home || ''
    }));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.title) {
      setError('Por favor complete los campos requeridos');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await ticketsAPI.create(formData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear el ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Nuevo Ticket de Incidente
        <Typography variant="body2" color="text.secondary">
          Complete los datos del incidente
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* ✅ BUSCADOR DE CLIENTES CON AUTOCOMPLETE */}
          <Grid item xs={12}>
            <ClienteBuscador
              value={selectedCustomer}
              onChange={handleSelectCustomer}
              label="Buscar Cliente *"
              placeholder="Nombre, apellido, cédula o teléfono..."
              required
              autoFocus
            />

            {selectedCustomer && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <strong>Cliente seleccionado:</strong> {selectedCustomer.nombres} {selectedCustomer.apellidos}
                {' - '}
                <Chip label={selectedCustomer.identificacion} size="small" />
                {selectedCustomer.estado && (
                  <Chip
                    label={`Estado: ${selectedCustomer.estado}`}
                    size="small"
                    color={
                      selectedCustomer.estado === 'ACTIVO' ? 'success' :
                      selectedCustomer.estado === 'SUSPENDIDO' ? 'warning' :
                      selectedCustomer.estado === 'RETIRADO' ? 'error' : 'default'
                    }
                    sx={{ ml: 1 }}
                  />
                )}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Título del Incidente"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ej: Corte de servicio, Intermitencia, etc."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción del Incidente"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describa el problema detalladamente..."
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                name="ticket_type"
                value={formData.ticket_type}
                onChange={handleChange}
                label="Tipo"
              >
                <MenuItem value="incidente">Incidente (Falla)</MenuItem>
                <MenuItem value="peticion">Petición (Cambio/Traslado)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Origen del Problema</InputLabel>
              <Select
                name="problem_origin"
                value={formData.problem_origin}
                onChange={handleChange}
                label="Origen del Problema"
              >
                <MenuItem value="corte_total">Corte Total</MenuItem>
                <MenuItem value="intermitencia">Intermitencia</MenuItem>
                <MenuItem value="lentitud">Lentitud / Latencia</MenuItem>
                <MenuItem value="dano_fisico">Daño Físico</MenuItem>
                <MenuItem value="configuracion">Configuración</MenuItem>
                <MenuItem value="otro">Otro</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Prioridad</InputLabel>
              <Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                label="Prioridad"
              >
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="media">Media</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Teléfono Contacto"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              onFocus={(e) => e.target.select()}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Teléfono Alternativo"
              name="contact_phone_alt"
              value={formData.contact_phone_alt}
              onChange={handleChange}
              onFocus={(e) => e.target.select()}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.customer_id || !formData.title}
        >
          {loading ? <CircularProgress size={24} /> : 'Crear Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
